//@flow
import * as dayjs from 'dayjs';
import type {OpUnitType} from 'dayjs';

export * from './timeAgo';

export function getStringDate(dateNumber: number | Date | string) {
  const date = new Date(dateNumber);

  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  return date.toLocaleDateString('es-ES', options);
}

export function formatDate(date: number | Date, format: string) {
  return dayjs(date).format(format);
}

export function formatDateTime(date: Date) {
  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    hour12: true,
    minute: 'numeric',
  };
  const intl = new Intl.DateTimeFormat('es-ES', options);

  return intl.format(date);
}

export const abvMonthStrings = [
  'En',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sept',
  'Oct',
  'Nov',
  'Dic',
];

export function toUTCString(
  date: Date,
  endDay: boolean = false,
  startDay: boolean = true
): string {
  const now = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    endDay ? 23 : startDay ? 0 : date.getHours(),
    endDay ? 59 : startDay ? 0 : date.getMinutes(),
    endDay ? 59 : startDay ? 0 : date.getSeconds()
  );

  const now_utc = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    now.getUTCHours(),
    now.getUTCMinutes(),
    now.getUTCSeconds()
  );
  const dateNumber = new Date(now_utc);
  return dateNumber.toISOString();
}

type DateUTCOptions = {
  endDay?: boolean,
  startDay?: boolean,
};

export function dateToUTCString(
  date: Date,
  options: DateUTCOptions = {}
): string {
  const {endDay = false, startDay = true} = options;
  return toUTCString(date, endDay, startDay);
}

export function alterDateByDays(
  offsetDays: number,
  date: Date = new Date(),
  add?: boolean = false
) {
  const cpDate = date;
  const diff = add ? date.getDate() + offsetDays : date.getDate() - offsetDays;
  const numTime = cpDate.setDate(diff);
  return new Date(numTime);
}

export function getFirstDayOfWeek(d: Date = new Date()) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -7 : 1);
  return new Date(date.setDate(diff));
}

export function formatTime(timestamp: number): string {
  const totalSeconds = Math.floor(timestamp / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  return `${hours} hrs : ${minutes.toString().padStart(2, '0')} min`;
}

type TimeLimitResult = {
  withinLimit: boolean,
  timeRemaining: number,
  percentageRemaining: number,
};

export function isWithinTimeLimit(
  date: Date,
  elapsedTime: number,
  unit: OpUnitType
): TimeLimitResult {
  const currentDate = dayjs();
  const dateToCompare = dayjs(date);

  const alteredDate = dateToCompare.add(elapsedTime, unit);

  const withinLimit = currentDate.isBefore(alteredDate);

  let percentageRemaining = 0;

  let timeRemaining = 0;

  if (withinLimit) {
    timeRemaining = alteredDate.diff(currentDate, 'millisecond');
    const unitRemaining = alteredDate.diff(currentDate, unit);
    const totalDuration = alteredDate.diff(dateToCompare, unit);
    percentageRemaining = (unitRemaining / totalDuration) * 100;
  }

  return {
    withinLimit,
    timeRemaining,
    percentageRemaining,
  };
}
export const getDiffDays = (date: Date) => dayjs().diff(dayjs(date), 'day');

export function getLastDayOfMonth(date: Date): number {
  const currentYear = date.getFullYear();
  const currentMonth = date.getMonth();

  return new Date(currentYear, currentMonth + 1, 0).getDate();
}

export function getWeekDayNames(locale: string = 'es-PE') {
  const days = new Array(7).fill(0).map((_, i) => i);

  const intl = new Intl.DateTimeFormat(locale, {weekday: 'short'});

  const weekDaysNames: string[] = days.map(day => {
    const date = new Date(Date.UTC(2021, 5, day));
    return intl.format(date);
  });

  return weekDaysNames;
}

export function convertToLocalTime(timeWithUtcOffset: string) {
  const dateInPeru = new Date(
    new Date().toISOString().split('T')[0] + 'T' + timeWithUtcOffset
  );

  const localTimeFormatter = new Intl.DateTimeFormat('es', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });

  const localTime = localTimeFormatter.format(dateInPeru);
  return localTime;
}

export const isValidDate = (date: any): boolean => {
  const parsedDate = new Date(date);
  return parsedDate instanceof Date && !isNaN(parsedDate.getTime());
};
